import type {
  CreatePriceExperienceRequest,
  CreatePriceMode,
  CreatePriceSessionResponse,
  SaveCreatePriceRequest,
  SaveCreatePriceResponse,
  SaveCreatePriceSessionRequest,
} from '@/api';
import { getApiClient } from '@/api/client';
import { type NormalizedError, normalizeApiError } from '@/utils/error-utils';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

export const useCheckForAlternativeVatForDate = (date: Date | null) =>
  useQuery({
    queryKey: ['checkForAlternativeVat', date],
    queryFn: async () => {
      if (!date) return false;
      const response = await getApiClient().alternativeVatCheck({ date });
      return response;
    },
    enabled: !!date,
  });

export const useCreatePriceExperience = (
  onError: (error: NormalizedError) => void,
) =>
  useMutation({
    mutationFn: async (payload: CreatePriceExperienceRequest) => {
      const response = await getApiClient().startCreatePriceExperience({
        createPriceExperienceRequest: payload,
      });
      return response;
    },
    onError: (err) => onError(normalizeApiError(err)),
  });

export const usePricePoints = (
  priceUnitId: number,
  planningYear: number,
  rrp: number,
) =>
  useQuery({
    queryKey: ['pricePoints', priceUnitId, planningYear, rrp],
    queryFn: async () => {
      const response = await getApiClient().pricesPricePoints({
        priceUnitId,
        planningYear,
        rrp,
      });
      return response;
    },
  });

export const useSaveCreatePrices = () =>
  useMutation({
    mutationFn: async (
      request: SaveCreatePriceRequest,
    ): Promise<SaveCreatePriceResponse> => {
      return getApiClient().saveCreatePrices({
        saveCreatePriceRequest: request,
      });
    },
  });

export const useGetCreatePriceSession = (mode: CreatePriceMode) =>
  useQuery<CreatePriceSessionResponse | null>({
    queryKey: ['createPriceSession', mode],
    queryFn: async () => {
      try {
        return await getApiClient().getCreatePriceSession({ mode });
      } catch (error: unknown) {
        if (
          typeof error === 'object' &&
          error !== null &&
          'status' in error &&
          (error as { status?: number }).status === 404
        ) {
          return null;
        }
        throw error;
      }
    },
    retry: false,
    staleTime: 0,
    refetchOnMount: 'always',
  });

export const useFetchCreatePriceSession = () =>
  useMutation({
    mutationFn: async (
      mode: CreatePriceMode,
    ): Promise<CreatePriceSessionResponse | null> => {
      try {
        return await getApiClient().getCreatePriceSession({ mode });
      } catch (error: unknown) {
        if (
          typeof error === 'object' &&
          error !== null &&
          'status' in error &&
          (error as { status?: number }).status === 404
        ) {
          return null;
        }
        throw error;
      }
    },
  });

export const useSaveCreatePriceSession = () =>
  useMutation({
    mutationFn: async (request: SaveCreatePriceSessionRequest) => {
      await getApiClient().saveCreatePriceSession({
        saveCreatePriceSessionRequest: request,
      });
    },
  });

export const useDeleteCreatePriceSession = (mode: CreatePriceMode) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      await getApiClient().deleteCreatePriceSession({ mode });
    },
    onSuccess: () => {
      queryClient.setQueryData(['createPriceSession', mode], null);
    },
  });
};

export const usePublishCreateAndGoToApprove = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (request: SaveCreatePriceSessionRequest) => {
      await getApiClient().publishCreateAndGoToApprove({
        saveCreatePriceSessionRequest: request,
      });
    },
    onSuccess: () => {
      queryClient.setQueryData(['createPriceSession', 'Create'], null);
      queryClient.setQueryData(['createPriceSession', 'Approve'], null);
    },
  });
};
